import pluralize from 'pluralize';
import { useCallback } from 'react';

import { type Clip } from '@air/api/types';
import { Button } from '@air/primitive-button';
import { Modal, ModalDescription, ModalTitle } from '@air/primitive-modal';
import { cn } from '@air/utils-tailwind';

export type BulkDownloadAssetsModal = {
  clipIds: Clip['id'][];
  onDownloadAsZip: () => void;
  onDownloadIndividually: () => void;
};

export const BulkDownloadAssetsModal = ({
  clipIds,
  onClose,
  onDownloadAsZip,
  onDownloadIndividually,
}: AirModalProps<BulkDownloadAssetsModal>) => {
  /**
   * This is an arbitrary number that we use to determine if we should show the option to download individually
   * or as a zip.
   *
   * @see https://air-labs-team.slack.com/archives/C04SUMD2ULF/p1740013219518209?thread_ts=1739988638.505229&cid=C04SUMD2ULF
   */
  const canDownloadIndividually = clipIds.length < 50;

  const handleDownloadAsZip = useCallback(() => {
    onDownloadAsZip();
    onClose();
  }, [onClose, onDownloadAsZip]);

  const handleDownloadIndividually = useCallback(() => {
    onDownloadIndividually();
    onClose();
  }, [onClose, onDownloadIndividually]);

  return (
    <Modal isOpen data-testid="BULK_DOWNLOAD_ASSETS_MODAL" onDismiss={onClose}>
      <div className="flex flex-col gap-4">
        <header className="flex flex-col gap-4">
          <ModalTitle>Download {pluralize('asset', clipIds.length, true)}?</ModalTitle>
          <ModalDescription>
            {canDownloadIndividually
              ? `You can download these ${pluralize(
                  'asset',
                  clipIds.length,
                  true,
                )} as individual files or a single zip. A zip offers some compression but may take a few minutes to generate.`
              : `Downloading more than ${pluralize(
                  'asset',
                  clipIds.length,
                  true,
                )} requires Air to prepare a zip file. This may take a few minutes. Your download will begin automatically once the zip is ready.`}
          </ModalDescription>
        </header>
        <footer className={cn('flex gap-2', canDownloadIndividually ? 'justify-end' : 'justify-between')}>
          <Button appearance="ghost" color="grey" onClick={onClose} size="large">
            Cancel
          </Button>
          {canDownloadIndividually ? (
            <div className="flex items-center gap-2">
              <Button
                appearance="outline"
                data-testid="BULK_DOWNLOAD_ASSETS_MODAL_ZIP_BUTTON"
                color="grey"
                onClick={handleDownloadAsZip}
                size="large"
              >
                Download as zip
              </Button>
              <Button
                appearance="filled"
                data-testid="BULK_DOWNLOAD_ASSETS_MODAL_INDIVIDUAL_BUTTON"
                onClick={handleDownloadIndividually}
                size="large"
              >
                Download individually
              </Button>
            </div>
          ) : (
            <Button
              appearance="filled"
              data-testid="BULK_DOWNLOAD_ASSETS_MODAL_ZIP_BUTTON"
              color="blue"
              onClick={handleDownloadAsZip}
              size="large"
            >
              Download
            </Button>
          )}
        </footer>
      </div>
    </Modal>
  );
};
